
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Inject, Prop } from 'vue-property-decorator';
import { PqtMeasurementSpecification, PqtMeasurementSpecificationTemplate, PqtMeasurementValueType } from '../../backend/backendapi';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import PqtMeasurementSpecificationEditModal from './PqtMeasurementSpecificationEditModal';
import { Like } from '../../../util/Like';
import { camelCase } from '../../../util';
import { formatValue, getValue } from '../util';
import MeasurementSpecificationsTemplateBadges from '../measurementSpecificationTemplates/MeasurementSpecificationsTemplateBadges';
import State from '../State';

export type PqtMeasurementSpecificationContainer = {
  measurementSpecifications?: PqtMeasurementSpecification[] | undefined;
}

@Component({
  components: {
    MeasurementSpecificationsTemplateBadges,
    MeasurementSpecificationTemplateSelectionModal: //circular dependency, needs to loaded this way (dynamically)
        () => import('../measurementSpecificationTemplates/MeasurementSpecificationTemplateSelectionModal.vue'),
    PqtMeasurementSpecificationEditModal,
    VueGoodTable,
  },
})
export default class PqtMeasurementSpecifications extends Vue {
  @Prop({ required: true })
  readonly container!: PqtMeasurementSpecificationContainer;
  @Prop({ required: true })
  readonly editable!: boolean;
  @Prop({ default: true })
  readonly showTemplateNames!: boolean;
  @Prop({ default: true })
  readonly inheritMedia!: boolean;

  @Inject({ from: 'state' })
  readonly state!: State;

  get columns(): unknown[] {
    let ret: unknown[] = [
      {
        label: this.$t('productQualityTests.measurementSpecification.model.name').toString(),
        field: (row: PqtMeasurementSpecification) => row.name,
      },
      {
        label: this.$t('productQualityTests.measurementSpecification.model.description').toString(),
        field: (row: PqtMeasurementSpecification) => row.description,
      },
      {
        label: this.$t('productQualityTests.measurementSpecification.model.type').toString(),
        field: (row: PqtMeasurementSpecification) => row.type,
        formatFn: (type: PqtMeasurementValueType) => this.$t('productQualityTests.measurementValueType.' + camelCase(PqtMeasurementValueType[type])).toString(),
      },
      {
        label: this.$t('productQualityTests.measurementSpecification.model.lowerBound').toString(),
        field: (row: PqtMeasurementSpecification) => row.type === PqtMeasurementValueType.Float ? getValue(row.lowerBound, row.type) : null,
        formatFn: (value: number | null) => formatValue(value, this.state.config),
      },
      {
        label: this.$t('productQualityTests.measurementSpecification.model.nominalValue').toString(),
        field: (row: PqtMeasurementSpecification) => getValue(row.nominalValue, row.type),
        formatFn: (value: number | string) => formatValue(value, this.state.config),
      },
      {
        label: this.$t('productQualityTests.measurementSpecification.model.upperBound').toString(),
        field: (row: PqtMeasurementSpecification) => row.type === PqtMeasurementValueType.Float ? getValue(row.upperBound, row.type) : null,
        formatFn: (value: number | null) => formatValue(value, this.state.config),
      },
      {
        label: this.$t('productQualityTests.measurementSpecification.model.required').toString(),
        field: (row: PqtMeasurementSpecification) => !row.optional,
        type: 'boolean',
      },
    ];
    if (this.editable) {
      ret.push({
        label: this.$t('general.actions').toString(),
        field: 'actions',
      });
    }
    return ret;
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get rows(): PqtMeasurementSpecification[] {
    return this.container.measurementSpecifications ?? [];
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  onClick(arg: { row: Like<PqtMeasurementSpecification>, pageIndex: number, selected: boolean, event: MouseEvent }): void {
    if (!this.editable) {
      return;
    }

    this.onEdit(arg.row);
  }

  onDelete(specification: Like<PqtMeasurementSpecification>): void {
    if (!this.container.measurementSpecifications) {
      return;
    }

    const index = this.container.measurementSpecifications.findIndex(e => e.id === specification.id);
    if (index >= 0) {
      this.container.measurementSpecifications.splice(index, 1);
    }
  }

  onEdit(specification: Like<PqtMeasurementSpecification>): void {
    if (!this.container.measurementSpecifications) {
      return;
    }
    const original = this.container.measurementSpecifications.find(e => e.id === specification.id);
    (this.$refs.editModal as PqtMeasurementSpecificationEditModal).open(original);
  }

  onNew(): void {
    this.container.measurementSpecifications ??= [];

    //new unique id
    const usedIds = new Set(this.container.measurementSpecifications.map(e => e.id));
    let id: string;
    do {
      id = crypto.randomUUID();
    }
    while (usedIds.has(id));

    //
    const specification = PqtMeasurementSpecification.fromJS(
        { id: id, type: PqtMeasurementValueType.Float, lowerBound: '0', nominalValue: '0', upperBound: '0' });
    this.container.measurementSpecifications.push(specification);
    (this.$refs.editModal as PqtMeasurementSpecificationEditModal).open(specification);
  }

  onTemplateChoose(): void {
    // needs to be done this way because circular dependency
    // readable code: 
    // (this.$refs.selectModal as MeasurementSpecificationTemplateSelectionModal).open();
    (this.$refs.selectModal as Vue & { open: () => void }).open();
  }

  onTemplateChosen(template: PqtMeasurementSpecificationTemplate): void {
    const mediaIds = (template.medias ?? []).map(e => e.mediaId);
    const specifications = (template.measurementSpecifications ?? []).map(e => {
      const ret = PqtMeasurementSpecification.fromJS(e);
      ret.id = crypto.randomUUID();
      ret.templateId = template.id;
      ret.templateName = template.name;
      if (this.inheritMedia && mediaIds.length) {
        ret.mediaIds = mediaIds;
      }
      return ret;
    });

    this.container.measurementSpecifications ??= [];
    this.container.measurementSpecifications.push(...specifications);

    // needs to be done this way because circular dependency
    // readable code: 
    // (this.$refs.selectModal as MeasurementSpecificationTemplateSelectionModal).dismiss();
    (this.$refs.selectModal as Vue & { dismiss: () => void }).dismiss();
  }

  saveSpecification(specification: PqtMeasurementSpecification): void {
    if (!this.container.measurementSpecifications) {
      return;
    }

    const index = this.container.measurementSpecifications.findIndex(e => e.id === specification.id);
    if (index < 0) {
      return;
    }

    this.container.measurementSpecifications.splice(index, 1, specification);
    (this.$refs.editModal as PqtMeasurementSpecificationEditModal).dismiss();
  }
}
